import React, { useState, useEffect } from 'react';
import './Makeoffer.css';
import { CONTACT_API_URL, hasContactApi } from './api'; // Imported API config
import emailjs from '@emailjs/browser'; // Imported EmailJS

const MakeOffer = ({ isOpen, onClose, property }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    offerAmount: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Reset form when modal opens
      setFormData({
        name: '',
        email: '',
        phone: '',
        offerAmount: '',
        message: '',
      });
      setShowSuccess(false);
      setIsSubmitting(false);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const propertyAddress = property ? `${property.address}, ${property.suburb}` : 'Unknown Property';
    
    // Construct a clear subject and message body containing the Offer Amount
    const emailSubject = `OFFER: $${formData.offerAmount} for ${propertyAddress}`;
    const detailedMessage = `
      PROPERTY: ${propertyAddress}
      OFFER AMOUNT: $${formData.offerAmount}
      
      BUYER MESSAGE/CONDITIONS:
      ${formData.message}
    `;

    // 1. Try Custom API
    if (hasContactApi()) {
      try {
        const res = await fetch(CONTACT_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            name: formData.name, 
            email: formData.email, 
            phone: formData.phone, 
            subject: emailSubject, 
            message: detailedMessage 
          }),
        });
        
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || `Request failed: ${res.status}`);
        }

        handleSuccess();
      } catch (err) {
        console.error('API offer submit failed:', err);
        // Don't alert here, let it fall through to user or handle silently if you prefer
        // For consistency with contact page, we just stop loading if it fails completely
        setIsSubmitting(false); 
      }
      return;
    }

    // 2. Fallback to EmailJS
    try {
      // EmailJS configuration
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'your_service_id';
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'your_template_id';
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'your_public_key';
      
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        subject: emailSubject,
        message: detailedMessage,
        offer_amount: formData.offerAmount, // Sending as separate param too in case template uses it
        property_address: propertyAddress,  // Sending as separate param too
        to_email: 'abhi@auzlandre.com.au'
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      handleSuccess();

    } catch (err) {
      console.error('EmailJS offer send failed:', err);
      alert('Failed to submit offer. Please try again or contact us directly.');
      setIsSubmitting(false);
    }
  };

  // Helper to handle UI updates on success
  const handleSuccess = () => {
      setIsSubmitting(false);
      setShowSuccess(true);
      // Close modal after a delay
      setTimeout(() => {
        onClose();
      }, 2000);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="offer-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          &times;
        </button>
        
        <div className="offer-header">
          <h3>Make an Offer</h3>
          {property && <p>For: {property.address}, {property.suburb}</p>}
        </div>

        {showSuccess ? (
            <div className="offer-success">
                <h4>Thank You!</h4>
                <p>Your offer has been submitted successfully.</p>
            </div>
        ) : (
            <form className="offer-form" onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your Name"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Your Phone Number"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="offerAmount">Offer Amount ($) *</label>
                    <input
                        type="number"
                        id="offerAmount"
                        name="offerAmount"
                        value={formData.offerAmount}
                        onChange={handleChange}
                        required
                        placeholder="e.g., 850000"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="message">Message / Conditions</label>
                    <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="4"
                        placeholder="Any conditions or notes for your offer..."
                    ></textarea>
                </div>
                <div className="form-actions">
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting...' : 'Submit Offer'}
                    </button>
                </div>
            </form>
        )}
      </div>
    </div>
  );
};

export default MakeOffer;